import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Row,
  Col,
  Image,
  Button,
  message,
  Modal,
  Select,
  Form,
  Typography,
} from "antd";
import blink from "../../assets/images/blink.png";
import { jobService } from "../../services";
import { formatDateDetail } from "../../helpers";
import { Job, JobSubmission, ListResponse, Pagination } from "../../types";

const { Text } = Typography;

const JobSubmissionsManagement = () => {
  const [submissionsData, setSubmissionsData] =
    useState<ListResponse<JobSubmission>>();
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    resultsPerPage: 10,
  });
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await jobService.getJobList({
          page: 1,
          resultsPerPage: 100,
        });
        setJobs(res.data);
      } catch (error) {
        message.error("Không thể tải danh sách việc làm: " + error);
      }
    };
    fetchJobs();
  }, []);

  const getSubmissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: any = { ...pagination };
      if (selectedJobId) params.jobId = selectedJobId;
      const response = await jobService.getJobSubmissionList(params);
      setSubmissionsData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách ứng tuyển: " + error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination, selectedJobId]);

  useEffect(() => {
    getSubmissions();
  }, [getSubmissions]);

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      ...pagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  const handleJobChange = (value: number) => {
    setSelectedJobId(value);
  };

  const handleDownloadCV = async (id: number) => {
    try {
      const url = await jobService.getJobSubmissionCVById(id);
      window.open(url, "_blank");
    } catch (error) {
      message.error("Không thể tải CV: " + error);
    }
  };

  const handlePreview = (coverLetter: string) => {
    setPreviewContent(coverLetter);
    setIsPreviewVisible(true);
  };

  const truncateMessage = (msg: string, maxLength = 20) => {
    if (msg.length <= maxLength) return msg;
    return msg.substring(0, maxLength) + "...";
  };

  const columns = [
    {
      title: "Họ và tên",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Số điện thoại",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Vị trí ứng tuyển",
      dataIndex: ["job", "title"],
      key: "jobTitle",
      render: (_: any, record: JobSubmission) =>
        record.job ? (
          record.job.title
        ) : (
          <Text type="secondary">Việc làm đã xoá</Text>
        ),
    },
    {
      title: "Phòng ban",
      dataIndex: ["job", "department", "name"],
      key: "departmentName",
      render: (_: any, record: JobSubmission) =>
        record.job?.department.name ?? "-",
    },
    {
      title: "CV",
      dataIndex: "cvFileName",
      key: "cvFileName",
      render: (_: any, record: JobSubmission) => (
        <Button
          type="link"
          onClick={() => handleDownloadCV(record.id)}
          style={{ padding: 0 }}
        >
          {record.cvFileName}
        </Button>
      ),
    },
    {
      title: "Ngày ứng tuyển",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value: string) => <Text>{formatDateDetail(value)}</Text>,
    },
    {
      title: "Thư xin việc",
      dataIndex: "coverLetter",
      key: "coverLetter",
      render: (text: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Text style={{ color: "#666" }}>{truncateMessage(text)}</Text>
          {text.length > 20 && (
            <Text
              style={{ color: "#1890ff", cursor: "pointer", fontSize: "12px" }}
              onClick={() => handlePreview(text)}
            >
              Xem chi tiết
            </Text>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <h1 className="page-title">
        Quản lý ứng tuyển <Image src={blink} preview={false} className="blink-icon" />
      </h1>
      <Row style={{ marginBottom: 16 }} align="middle">
        <Col span={24} style={{ marginTop: 10 }}>
          <Form layout="inline">
            <Text
              style={{
                fontSize: 16,
                fontWeight: 600,
                marginTop: 8,
                marginRight: 8,
              }}
            >
              Vị trí ứng tuyển
            </Text>
            <Form.Item style={{ marginBottom: 0, marginLeft: 8 }}>
              <Select
                showSearch
                allowClear
                placeholder="Chọn vị trí ứng tuyển"
                style={{ minWidth: 220 }}
                value={selectedJobId}
                onChange={handleJobChange}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={jobs.map((job) => ({
                  label: job.title,
                  value: job.id,
                }))}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={submissionsData ? submissionsData.data : []}
        pagination={{
          current: pagination.page,
          pageSize: pagination.resultsPerPage,
          total: submissionsData ? submissionsData.totalResults : 0,
          onChange: handlePaginationChange,
          showQuickJumper: true,
        }}
        loading={isLoading}
        scroll={{ x: "max-content" }}
      />
      <Modal
        title="Xem thư xin việc"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        footer={null}
        width={600}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            border: "1px solid #e9ecef",
            whiteSpace: "pre-wrap",
            lineHeight: "1.6",
          }}
        >
          {previewContent}
        </div>
      </Modal>
    </div>
  );
};

export default JobSubmissionsManagement;
